import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress, Button, Paper } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';

interface ProfileGuardProps {
  children: React.ReactNode;
}

/**
 * ProfileGuard - Controls dashboard access based on agency onboarding status
 * 
 * Flow:
 * 1. Phone not verified → Redirect to login
 * 2. Phone verified, no basic info → Redirect to /basic-info
 * 3. Has basic info → Allow dashboard access
 * 4. Dashboard shows prompts to complete profile if incomplete
 */
const ProfileGuard: React.FC<ProfileGuardProps> = ({ children }) => {
  const { user, userType, isLoading } = useAuthStore();

  // Show loading while checking auth
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // No user and not an employee - redirect to login
  if (!user && userType !== 'employee') {
    return <Navigate to="/login" replace />;
  }

  // If this is an employee, skip agency profile checks
  if (userType === 'employee') {
    return <>{children}</>;
  }

  // Check if phone is verified
  if (user && !user.phone_verified) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Warning color="warning" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Phone Verification Required
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Please verify your phone number to continue.
          </Typography>
          <Button variant="contained" onClick={() => window.location.href = '/login'}>
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  // Only redirect if basic info is truly required
  if (user && (user.status === 'basic_info_required' || !user.requirements?.hasBasicInfo)) {
    return <Navigate to="/basic-info" replace />;
  }

  // Do not force users into onboarding here.
  // The dashboard can surface onboarding prompts without trapping navigation.
  return <>{children}</>;
};

export default ProfileGuard;
